import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import type {
    AreaDescriptionDocType,
    DiaryDocType,
    OverallDescriptionDocType,
    SampleDocType
} from '@ast-data/shared';
import { useFamilyDocs } from '../../db/useScopedDocs';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { useFamilyDescriptions } from '../findings/useFamilyDescriptions';
import { useFindingRefResolver } from '../findings/findingRefLabels';
import { areaDescriptionLocation } from '../findings/areaDescriptionLabel';
import { useDiaryLabel } from '../findings/useDiaryLabel';
import { sampleLabel } from '../findings/sampleLabel';

/** How a place a picture hangs in reads, and where its dialog is. */
export interface MediaPlaceLabel {
    label: string;
    to: string;
}

/**
 * Label and route for every description of the family a picture can hang in —
 * "Bef. 7000, Fl. 1, Pl. 1", "Gesamtbeschreibung 3", "Flächenbeschreibung
 * Fl. 3, Pl. 1", "Probe 12", "Tagebuch 26.09.2026". Family-wide because links
 * are: a picture may come from a sibling phase's Befund.
 */
export function useMediaPlaceLabels(
    projectId: string
): Map<string, MediaPlaceLabel> {
    const intl = useIntl();
    const inField = useLocation().pathname.startsWith('/field');
    const { findings, planums, profiles } = useFamilyDescriptions(projectId);
    const refs = useFindingRefResolver(findings, planums, profiles);
    const familyIds = useFamilyProjectIds(projectId);
    const { results: overallDescriptions } =
        useFamilyDocs<OverallDescriptionDocType>(
            'overall_descriptions',
            familyIds
        );
    const { results: areaDescriptions } = useFamilyDocs<AreaDescriptionDocType>(
        'area_descriptions',
        familyIds
    );
    const { results: samples } = useFamilyDocs<SampleDocType>(
        'samples',
        familyIds
    );
    const { results: diaries } = useFamilyDocs<DiaryDocType>(
        'diaries',
        familyIds
    );
    const diaryLabel = useDiaryLabel(projectId, familyIds);

    return useMemo(() => {
        const labels = new Map<string, MediaPlaceLabel>(refs);
        for (const description of overallDescriptions) {
            labels.set(description.id, {
                label: intl.formatMessage(
                    { id: 'overallDescription.label' },
                    { number: description.overallDescriptionNumber }
                ),
                to: inField
                    ? `/field/${description.projectId}/overall-descriptions/${description.id}/edit`
                    : `/office/projects/${description.projectId}/overall-descriptions/${description.id}/edit`
            });
        }
        for (const description of areaDescriptions) {
            labels.set(description.id, {
                label: intl.formatMessage(
                    { id: 'areaDescription.label' },
                    { location: areaDescriptionLocation(description) }
                ),
                to: inField
                    ? `/field/${description.projectId}/area-descriptions/${description.id}/edit`
                    : `/office/projects/${description.projectId}/area-descriptions/${description.id}/edit`
            });
        }
        for (const sample of samples) {
            labels.set(sample.id, {
                label: sampleLabel(intl, sample),
                to: inField
                    ? `/field/${sample.projectId}/samples/${sample.id}/edit`
                    : `/office/projects/${sample.projectId}/samples/${sample.id}/edit`
            });
        }
        for (const diary of diaries) {
            labels.set(diary.id, {
                label: diaryLabel(diary),
                to: inField
                    ? `/field/${diary.projectId}/diaries/${diary.id}/edit`
                    : `/office/projects/${diary.projectId}/diaries/${diary.id}/edit`
            });
        }
        return labels;
    }, [
        refs,
        overallDescriptions,
        areaDescriptions,
        samples,
        diaries,
        diaryLabel,
        intl,
        inField
    ]);
}
